/**
 * "Page rules" — Dashboard section.
 *
 * Every claim here is checked against shipped behaviour, per the plan's
 * acceptance bar. Two things it deliberately DOES say, because the surface's own
 * review found operators discovering them in production instead:
 *
 * - the complete match-operator semantics table (which comparisons ignore case,
 *   the query string and a trailing slash), and
 * - the per-behaviour resolution model with a worked example, since "highest
 *   priority wins" is true per BEHAVIOUR and false per rule.
 *
 * Live re-evaluation on client-routed sites SHIPPED with SYN-391, so the limits
 * section now says rules follow a route change — and still names the residual
 * (a router that changes the address in a way no signal reports), because the
 * detection is signal-based rather than universal.
 */
import type { DocPageContent } from '../types';

export const DASHBOARD_PAGE_RULES_PAGE: DocPageContent = {
  slug: 'dashboard/page-rules',
  titleKey: 'docs.dashboard.pagerules.title',
  descriptionKey: 'docs.dashboard.pagerules.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.dashboard.pagerules.lead' },

    {
      kind: 'heading',
      level: 2,
      id: 'what-you-can-change',
      textKey: 'docs.dashboard.pagerules.axes.heading',
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.pagerules.axes.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.pagerules.axes.item.availability',
        'docs.dashboard.pagerules.axes.item.actions',
        'docs.dashboard.pagerules.axes.item.starters',
        'docs.dashboard.pagerules.axes.item.theme',
        'docs.dashboard.pagerules.axes.item.persona',
        'docs.dashboard.pagerules.axes.item.tools',
      ],
    },

    {
      kind: 'heading',
      level: 2,
      id: 'where-it-applies',
      textKey: 'docs.dashboard.pagerules.match.heading',
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.pagerules.match.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.pagerules.match.item.exact',
        'docs.dashboard.pagerules.match.item.startsWith',
        'docs.dashboard.pagerules.match.item.contains',
        'docs.dashboard.pagerules.match.item.glob',
      ],
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.pagerules.match.ignores' },

    {
      kind: 'heading',
      level: 2,
      id: 'when-rules-overlap',
      textKey: 'docs.dashboard.pagerules.precedence.heading',
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.pagerules.precedence.body' },
    { kind: 'paragraph', textKey: 'docs.dashboard.pagerules.precedence.example' },
    { kind: 'paragraph', textKey: 'docs.dashboard.pagerules.precedence.tools' },

    {
      kind: 'heading',
      level: 2,
      id: 'starting-points',
      textKey: 'docs.dashboard.pagerules.recipes.heading',
    },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.pagerules.recipes.item.checkout',
        'docs.dashboard.pagerules.recipes.item.admin',
        'docs.dashboard.pagerules.recipes.item.pricing',
      ],
    },

    { kind: 'heading', level: 2, id: 'limits', textKey: 'docs.dashboard.pagerules.limits.heading' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.pagerules.limits.item.cache',
        'docs.dashboard.pagerules.limits.item.spa',
        'docs.dashboard.pagerules.limits.item.public',
        'docs.dashboard.pagerules.limits.item.serverAxes',
        'docs.dashboard.pagerules.limits.item.count',
      ],
    },
  ],
};
